import {
  BILLING_CURRENCY_OPTIONS,
  stripeCurrencyToSymbol,
  symbolToStripeCurrency,
} from './stripeCurrency';
import { isTieredPrice, resolveBillingUnit } from './stripeBanding';

/**
 * Label one Stripe Price for a picker option. Mirrors `NewAgreement.js`:
 * tiered Prices have `unit_amount: null`, so they show banding unit + tier count.
 * @param {object} product
 * @param {object} price
 * @returns {string}
 */
const formatPriceOptionLabel = (product, price) => {
  const recurring = price.recurring
    ? ` / ${price.recurring.interval_count || 1}${price.recurring.interval}`
    : '';
  const nickname = price.nickname ? ` · ${price.nickname}` : '';

  let amount;
  if (isTieredPrice(price)) {
    const { label } = resolveBillingUnit(price, product);
    const tierCount = Array.isArray(price.tiers) ? price.tiers.length : 0;
    amount = `banded by ${label || 'tier'}${tierCount ? ` — ${tierCount} tiers` : ''}`;
  } else if (typeof price.unit_amount === 'number') {
    amount = `${stripeCurrencyToSymbol(price.currency)}${(price.unit_amount / 100).toFixed(2)}`;
  } else {
    amount = '—';
  }

  return `${product.name} — ${amount}${recurring}${nickname}`;
};

/**
 * Flatten Stripe products into picker options (one option per Price).
 * Per-unit amounts arrive in Stripe minor units, so labels divide by 100.
 * @param {object[]} products
 * @returns {object[]}
 */
export const flattenStripeProductsToPriceOptions = (products = []) => {
  const options = [];
  products.forEach((product) => {
    (product.prices || []).forEach((price) => {
      options.push({ product, price, value: price.id, label: formatPriceOptionLabel(product, price) });
    });
  });
  return options;
};

/**
 * Build POST /my/consultant/clients or /my/agreements line items from wizard rows.
 * @param {object[]} lineItems
 * @param {object[]} priceOptions
 * @returns {object[]}
 */
export const buildAgreementLineItemsPayload = (lineItems, priceOptions = []) =>
  lineItems.map((row) => {
    const base = {
      kind: row.kind,
      quantity: Number(row.quantity) || 1,
      description: row.description || undefined,
    };

    if (row.kind === 'subscription') {
      base.stripeProductId = row.stripeProductId;
      base.stripePriceId = row.stripePriceId;
      base.currency = row.currency;
      base.billingFrequency = row.billingFrequency || 'annual';

      const picked = priceOptions.find((o) => o.price?.id === row.stripePriceId);
      if (picked && isTieredPrice(picked.price)) {
        base.bandingQuantity = Number(row.bandingQuantity);
      } else if (row.unitAmount !== '' && row.unitAmount != null) {
        base.unitAmount = Number(row.unitAmount) || 0;
      }
    } else if (row.kind === 'one_off') {
      base.unitAmount = Number(row.unitAmount) || 0;
      if (row.currency) base.currency = row.currency;
    }

    return base;
  });

export const buildIndexationPolicyPayload = (policy) => {
  if (!policy || policy.kind === 'none') return { kind: 'none' };
  const noticeDays = Number(policy.noticeDays) || 0;
  const floorPercent = Number(policy.floorPercent) || 0;
  if (policy.kind === 'fixed_percent') return { kind: 'fixed_percent', floorPercent, noticeDays };
  if (policy.kind === 'cpi_with_floor') {
    return { kind: 'cpi_with_floor', floorPercent, noticeDays, cpiSource: policy.cpiSource || 'UK_CPIH' };
  }
  return { kind: 'none' };
};

export const defaultSubscriptionLine = () => ({
  kind: 'subscription',
  billingFrequency: 'annual',
  unitAmount: '',
  quantity: 1,
  description: '',
  stripeProductId: '',
  stripePriceId: '',
  currency: '',
  bandingQuantity: '',
});

export { BILLING_CURRENCY_OPTIONS, symbolToStripeCurrency };
